import * as React from "react";
import { useTheme } from "@mui/material/styles";
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  Checkbox,
  FormControlLabel,
  Button,
  TextField,
  Card,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { createClinicalService } from "../API/FacilitySettingsApi"; // Adjust the import path as needed
import AuthContext from "../Context/AuthProvider"; // Adjust the import path as needed
import { useContext } from "react";
import MenuBar from "../common/MenuBar";
import "../common/styles/CommonStyles.css"; // Import your custom styles
import GenericTextField from "../common/GenericTextField";
import HeaderValueDisplay from "../common/HeaderValueDisplay";
import GenericDatePicker from "../common/GenericDatePicker";
import GenericDropDown from "../common/GenericDropDown";
import GenericFileUploader from "../common/GenericFileUploader";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const locationOptions = ["Clinic", "Online", "Home Visit"];
const bookingTypeOptions = ["Walk-In", "Prebooking", "Both"];

function getStyles(option, selectedOptions, theme) {
  return {
    fontWeight: selectedOptions.includes(option)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  };
}

function NewServiceForm({ onSave }) {
  const theme = useTheme();
  const auth = useContext(AuthContext); // Access the authentication context
  const [serviceDetails, setServiceDetails] = React.useState({
    sourceCode: "",
    name: "",
    fees: "",
    feeConfirmed: false,
    duration: "",
    description: "",
    schedulingByPatient: false,
    isActive: true, // Default checked
    canBePrimary: true, // Default checked
    allowedBookingType: [],
    allowedLocations: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setServiceDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setServiceDetails((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleMultiSelectChange = (name, value) => {
    setServiceDetails((prev) => ({
      ...prev,
      [name]: typeof value === "string" ? value.split(",") : value,
    }));
  };

  const handleSubmit = async () => {
    try {
      // Prepare the payload for the API call
      const payload = {
        ...serviceDetails,
        facilityId: auth.auth.selectedFacility.id, // Add facility ID from the auth context
      };

      // Call the API to create a new clinical service
      const result = await createClinicalService(auth.auth, payload);

      if (result?.isSuccess) {
        console.log("Service created successfully.");
        if (onSave) {
          onSave(result.data); // Pass the saved data back to the parent component
        }
      } else {
        console.error("Error occurred while saving the service:", result?.data);
      }
    } catch (error) {
      console.error("An error occurred:", error);
    }
  };

  return (
    <Grid2 container size={12} direction="column">
      <Grid2 container size={12}>
        <Grid2 size={12}>
          <MenuBar showSave={true} onSave={handleSubmit} />
        </Grid2>
      </Grid2>
      <Grid2 size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }} container>
        <Card
          className="outerCard"
          variant="outlined"
          sx={{ padding: "5px", marginTop: "5px" }}
        >
          <Grid2
            container
            size={12}
            spacing={1}
            sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
          >
            <Grid2 size={12}>
              <GenericTextField
                label="Service Code"
                name="sourceCode"
                value={serviceDetails.sourceCode}
                onChange={handleChange}
              />
            </Grid2>

            <Grid2 size={12}>
              <GenericTextField
                label="Service Name"
                name="name"
                value={serviceDetails.name}
                onChange={handleChange}
              />
            </Grid2>

            {/* Fees & Allow Fees Editing */}
            <Grid2 size={12} container alignItems="center" spacing={2}>
              <Grid2 size={6}>
                <GenericTextField
                  label="Fees (INR)"
                  name="fees"
                  type="number"
                  value={serviceDetails.fees}
                  onChange={handleChange}
                />
              </Grid2>
              <Grid2 size={6} backgroundColor="white" mb={5}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={serviceDetails.feeConfirmed}
                      onChange={handleCheckboxChange}
                      name="feeConfirmed"
                    />
                  }
                  label="Allow Fees Editing"
                />
              </Grid2>
            </Grid2>

            <Grid2 size={12}>
              <GenericTextField
                label="Duration (Minutes)"
                name="duration"
                type="number"
                value={serviceDetails.duration}
                onChange={handleChange}
              />
            </Grid2>

            <Grid2 size={12}>
              <GenericTextField
                label="Description"
                name="description"
                value={serviceDetails.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid2>

            {/* Allowed Locations & Allowed Booking Type */}
            <Grid2 size={12} container alignItems="center" spacing={2}>
              <Grid2 size={6}>
                <GenericDropDown
                  label="Allowed Locations"
                  value={serviceDetails.allowedLocations}
                  onChange={(e) =>
                    handleMultiSelectChange("allowedLocations", e.target.value)
                  }
                  options={locationOptions}
                  name="allowedLocations"
                  multiple
                  fullWidth
                />
              </Grid2>

              <Grid2 size={6}>
                <GenericDropDown
                  label="Allowed Booking Type"
                  value={serviceDetails.allowedBookingType}
                  onChange={(e) =>
                    handleMultiSelectChange(
                      "allowedBookingType",
                      e.target.value
                    )
                  }
                  options={bookingTypeOptions}
                  name="allowedBookingType"
                  multiple
                  fullWidth
                />
              </Grid2>
            </Grid2>

            {/* Is Active & Can Be Primary */}
            <Grid2 size={12} container alignItems="center" spacing={2}>
              <Grid2 size={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={serviceDetails.isActive}
                      onChange={handleCheckboxChange}
                      name="isActive"
                    />
                  }
                  label="Is Active"
                />
              </Grid2>
              <Grid2 size={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={serviceDetails.canBePrimary}
                      onChange={handleCheckboxChange}
                      name="canBePrimary"
                    />
                  }
                  label="Can Be Primary"
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </Card>
      </Grid2>
    </Grid2>
  );
}

export default NewServiceForm;
